import React, { useEffect, useMemo, useRef, useState } from "react"
import { LexicalText } from "@/schema"
import { getUserReferenceAllowedUserIds } from "@/utils/custom-field-users"
import { normalizeUrl } from "@/utils/normalize-url"
import { BaseInlineInput, InlineDatePicker, InlineInput, InlineNumberInput, InlineSelect } from "@ui/forms/inline"
import { InlineUserSelect } from "@ui/forms/inline/selects/user"
import { MultiSelectInput } from "@ui/forms/inputs/multi-select-input"
import { Checkbox } from "@ui/shad"
import { ExternalLink, LoaderCircle, Pencil } from "lucide-react"

import type { CrossReferenceValue } from "@/components/cross-references/cross-reference-chip"
import { Editor } from "@/components/lexical"

import { CrossReferenceInlineField } from "./cross-reference-field"
import { SelectOptionDot } from "./select-option"
import type { CustomFieldInputProps } from "./types"

function RichTextInlineField({ customField, value, onSave, label, fixedLabelWidth }: CustomFieldInputProps) {
  const contentRef = useRef<string | null>(null)
  const dirtyRef = useRef(false)
  const initializedRef = useRef(false)
  const initialContent = useMemo<LexicalText | undefined>(
    () => (value ? { html: String(value) } : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth} alignLabel="start">
      <div
        className="hover:bg-muted focus-within:border-input relative w-full cursor-pointer overflow-hidden rounded-sm border border-transparent px-2 py-1 transition-colors focus-within:overflow-visible"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            if (dirtyRef.current) {
              onSave(contentRef.current || null)
              dirtyRef.current = false
            }
          }
        }}
      >
        <Editor
          initialContent={initialContent}
          onChange={(lexicalText) => {
            contentRef.current = lexicalText.html ?? null
            if (initializedRef.current) {
              dirtyRef.current = true
            }
            initializedRef.current = true
          }}
          startingPlaceholder={`Set ${customField.display_name.toLowerCase()}...`}
          disableHeadings
          disableSlashCommands
          styleOverrides={{
            "--lexical-editor-font-size": "13px",
            "--lexical-editor-max-height": "12rem",
            "--lexical-editor-min-height": "0",
          }}
        />
      </div>
    </BaseInlineInput>
  )
}

function UrlInlineField({ customField, value, onSave, isLoading, label, fixedLabelWidth }: CustomFieldInputProps) {
  const stringValue = (value as string) ?? ""
  const [isEditing, setIsEditing] = useState(false)
  const editContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isEditing) {
      const input = editContainerRef.current?.querySelector("input")
      input?.focus()
    }
  }, [isEditing])

  if (isEditing || !stringValue) {
    return (
      <div ref={editContainerRef} className="flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <InlineInput
            label={label}
            fixedLabelWidth={fixedLabelWidth}
            defaultValue={stringValue}
            placeholder={`Set ${customField.display_name.toLowerCase()}...`}
            isSaving={isLoading}
            autoComplete="off"
            inputClasses={isEditing ? "border-input ring-ring shadow-sm ring-1" : undefined}
            onSave={(v) => {
              onSave(v || null)
              setIsEditing(false)
            }}
          />
        </div>
        {stringValue && (
          <a
            href={normalizeUrl(stringValue)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${customField.display_name} in a new tab`}
            className="text-muted-foreground hover:text-foreground shrink-0 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="size-3.5" />
          </a>
        )}
      </div>
    )
  }

  return (
    <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth}>
      <div className="group/url flex w-full min-w-0 items-center gap-1">
        <a
          href={normalizeUrl(stringValue)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 min-w-0 flex-1 truncate pl-2 text-sm underline"
          onClick={(e) => e.stopPropagation()}
        >
          {stringValue}
        </a>
        <button
          type="button"
          aria-label={`Edit ${customField.display_name}`}
          className="text-muted-foreground hover:text-foreground hidden shrink-0 p-1 group-hover/url:block"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="size-3" />
        </button>
        <a
          href={normalizeUrl(stringValue)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${customField.display_name} in a new tab`}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded-sm p-1 focus-visible:ring-1 focus-visible:outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-3.5" />
        </a>
      </div>
    </BaseInlineInput>
  )
}

export function CustomFieldInput({
  customField,
  value,
  onSave,
  isLoading,
  label,
  fixedLabelWidth,
  multiSelectDisplay = "input",
}: CustomFieldInputProps) {
  switch (customField.field_type) {
    case "text":
      return (
        <InlineInput
          label={label}
          fixedLabelWidth={fixedLabelWidth}
          defaultValue={(value as string) ?? ""}
          placeholder={`Set ${customField.display_name.toLowerCase()}...`}
          isSaving={isLoading}
          autoComplete="off"
          tooltipOnOverflow
          onSave={(value) => onSave(value || null)}
        />
      )

    case "rich_text":
      return (
        <RichTextInlineField
          customField={customField}
          value={value}
          onSave={onSave}
          isLoading={isLoading}
          label={label}
          fixedLabelWidth={fixedLabelWidth}
        />
      )

    case "number":
      return (
        <InlineNumberInput
          format={customField.format}
          currencyCode={customField.currency_code}
          label={label}
          fixedLabelWidth={fixedLabelWidth}
          defaultValue={String(value ?? "")}
          placeholder={`Set ${customField.display_name.toLowerCase()}...`}
          isSaving={isLoading}
          autoComplete="off"
          onSave={(val) => onSave(val)}
        />
      )

    case "date": {
      const parseLocalDate = (dateStr: string): Date | undefined => {
        if (!dateStr) return undefined
        const [year, month, day] = dateStr.split("-").map(Number)
        if (!year || !month || !day) return undefined
        return new Date(year, month - 1, day)
      }

      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }

      return (
        <InlineDatePicker
          label={label}
          fixedLabelWidth={fixedLabelWidth}
          value={value ? parseLocalDate(String(value)) : undefined}
          placeholder={`Set ${customField.display_name.toLowerCase()}...`}
          onSave={(date) => onSave(date ? formatLocalDate(date as Date) : null)}
        />
      )
    }

    case "boolean":
      return (
        <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth}>
          <div className="flex h-8 items-center px-2">
            <Checkbox
              checked={(value as boolean) ?? false}
              onCheckedChange={(checked) => onSave(checked)}
              disabled={isLoading}
            />
            {isLoading && <LoaderCircle className="ml-2 h-4 w-4 animate-spin" />}
          </div>
        </BaseInlineInput>
      )

    case "single_select": {
      const options =
        customField.options?.select_options?.map((option) => ({
          label: (
            <span className="flex items-center gap-2">
              <SelectOptionDot customField={customField} option={option} />
              {option}
            </span>
          ),
          textLabel: option,
          value: option,
        })) || []
      return (
        <InlineSelect
          label={label}
          fixedLabelWidth={fixedLabelWidth}
          defaultValue={(value as string) ?? null}
          placeholder={`Set ${customField.display_name.toLowerCase()}...`}
          options={options}
          isSaving={isLoading}
          onSave={(value) => onSave(value)}
          clearable
        />
      )
    }

    case "multi_select": {
      const options =
        customField.options?.select_options?.map((option) => ({
          label: (
            <span className="flex items-center gap-2">
              <SelectOptionDot customField={customField} option={option} />
              {option}
            </span>
          ),
          textLabel: option,
          value: option,
        })) || []
      const selectedValues = Array.isArray(value) ? value : []
      const selectedOptions = options.filter((option) => selectedValues.includes(option.value))

      return (
        <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth}>
          <MultiSelectInput
            id={`custom-field-${customField.field_key}`}
            values={selectedValues}
            options={options}
            placeholder={`Set ${customField.display_name.toLowerCase()}...`}
            disabled={isLoading}
            onChange={onSave}
            size="sm"
            triggerButton={
              multiSelectDisplay === "pills" ? (
                <button
                  type="button"
                  disabled={isLoading}
                  className="hover:bg-muted focus-visible:ring-ring flex min-h-7 w-full flex-wrap items-center gap-1 rounded-sm px-2 py-1 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:opacity-50"
                >
                  {selectedOptions.length === 0 ? (
                    <span className="text-muted-foreground truncate text-sm">
                      Set {customField.display_name.toLowerCase()}...
                    </span>
                  ) : (
                    selectedOptions.map((option) => (
                      <span
                        key={option.value}
                        className="bg-secondary text-secondary-foreground flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-xs"
                      >
                        <SelectOptionDot customField={customField} option={option.value} />
                        <span className="truncate">{option.textLabel}</span>
                      </span>
                    ))
                  )}
                </button>
              ) : undefined
            }
          />
        </BaseInlineInput>
      )
    }

    case "cross_reference":
      return (
        <CrossReferenceInlineField
          customField={customField}
          value={value as CrossReferenceValue | null}
          onSave={onSave}
          isLoading={isLoading}
          label={label}
          fixedLabelWidth={fixedLabelWidth}
        />
      )

    case "url":
      return (
        <UrlInlineField
          customField={customField}
          value={value}
          onSave={onSave}
          isLoading={isLoading}
          label={label}
          fixedLabelWidth={fixedLabelWidth}
        />
      )

    case "user_reference":
      return (
        <InlineUserSelect
          label={label}
          selectedUserId={(value as string | null) ?? null}
          setSelectedUserId={() => {}}
          onSave={(userId) => onSave(userId)}
          isDisabled={isLoading}
          fixedLabelWidth={fixedLabelWidth}
          allowedUserIds={getUserReferenceAllowedUserIds(customField)}
        />
      )

    default:
      return (
        <BaseInlineInput label={label} fixedLabelWidth={fixedLabelWidth}>
          <span className="text-muted-foreground text-sm">Unsupported field type</span>
        </BaseInlineInput>
      )
  }
}
